import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import _ from 'lodash';
import { KEY_CODES } from '@/main/app.constants';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { Icon } from '@seeqdev/qomponents';
import type { JSX } from 'react';

export interface ButtonItem<T extends string = string> {
  label: string;
  id: string;
  value: T;
  disabled?: boolean;
  tooltip?: string;
  icon?: string;
}

/**
 * An input component with a group of buttons corresponding to selectable options
 */
export interface ButtonGroupProps<T extends string = string> {
  buttonItems: ButtonItem<T>[];
  name: string;
  value: T;
  onChange: (value: T) => void;
  testId?: string;
}

export const ButtonGroup = <T extends string = string>({
  buttonItems,
  name,
  value,
  onChange,
  testId,
}: ButtonGroupProps<T>): JSX.Element => {
  const selected = useMemo(() => value || buttonItems[0]?.value, [value, buttonItems]);
  const [tabIndex, setTabIndex] = useState(-1);
  const { t } = useTranslation();

  useEffect(() => {
    document.querySelector<HTMLInputElement>(`[data-index="${name}-${tabIndex}"`)?.focus?.();
  }, [tabIndex, name]);

  const renderButtonItem = (buttonItem: ButtonItem<T>, index: number) => {
    const item = (
      <button
        data-name={name}
        data-index={`${name}-${index}`}
        type="button"
        key={buttonItem.id}
        id={buttonItem.id}
        onClick={() => {
          if (!buttonItem.disabled) {
            onChange(buttonItem.value);
            setTabIndex(index);
          }
        }}
        onFocus={() => setTabIndex(index)}
        onKeyDown={(e) => e.keyCode === KEY_CODES.ENTER && !buttonItem.disabled && onChange(buttonItem.value)}
        tabIndex={index === tabIndex ? 0 : undefined}
        className={classNames(
          'sq-btn sq-btn-outline  sq-btn-xs cursorPointer',
          'font-weight-normal pt3 min-height-25 min-width-70 default-layer text-center',
          {
            'disabledLook forceCursorNotAllowed': buttonItem.disabled,
            'active sq-btn-theme': buttonItem.value === selected,
          },
        )}>
        {buttonItem.icon && (
          <Icon icon={buttonItem.icon} extraClassNames={classNames('mr5', { white: buttonItem.value === selected })} />
        )}
        {buttonItem.label && t(buttonItem.label)}
      </button>
    );

    if (buttonItem.tooltip) {
      return (
        <HoverTooltip text={buttonItem.tooltip} key={buttonItem.id}>
          {item}
        </HoverTooltip>
      );
    }

    return item;
  };

  return (
    <div
      className="sq-btn-group mt5"
      data-testid={testId}
      onKeyDown={(e) => {
        if (e.keyCode === KEY_CODES.RIGHT_ARROW) {
          setTabIndex((tabIndex) => (tabIndex < buttonItems.length - 1 ? tabIndex + 1 : tabIndex));
        } else if (e.keyCode === KEY_CODES.LEFT_ARROW) {
          setTabIndex((tabIndex) => (tabIndex > 0 ? tabIndex - 1 : tabIndex));
        }
      }}>
      {_.map(buttonItems, renderButtonItem)}
    </div>
  );
};
